/**
 * Turning a pitch class into a real, written chord.
 *
 * The Chord FIB picker offers twelve choices, not eighty-five: a pitch class with
 * no octave attached. But engraving and playback both need a register, and a chord
 * still has to be *spelled* correctly — the 7th of a major seventh chord is a
 * seventh, so it must sit on the seventh letter, whatever that forces its
 * accidental to be.
 *
 * So two decisions happen here, both hidden from the learner:
 *   1. Which spelling of the chosen pitch class produces the most readable chord.
 *   2. Which octave puts that chord comfortably inside the selected clef.
 */

import {
  LETTERS,
  isAwkwardSpelling,
  midi,
  naturalPitchClass,
  pitchName,
  type Letter,
  type Pitch,
} from './pitch';
import { solveRootFromDegree, spellChord, type ChordQuality, type ChordTone } from './chords';
import { CLEFS, CLEF_INFO, positionToPitch, type Clef } from './staff';

export type AccidentalPreference = 'sharp' | 'flat';

/**
 * Display names for the twelve pitch classes, under each spelling preference.
 *
 * Written out as literal strings this was a third accidental table, free to
 * drift from `pitchName` and from the staff. Deriving it means the picker's
 * label and the notehead the learner then sees are produced by the same code:
 * the natural letter for the seven white keys, and the neighbour a semitone
 * away — below for a sharp, above for a flat — for the five black ones.
 */
const PITCH_CLASS_NAMES: Record<AccidentalPreference, string[]> = {
  sharp: buildPitchClassNames(1),
  flat: buildPitchClassNames(-1),
};

function buildPitchClassNames(alter: 1 | -1): string[] {
  const names: string[] = [];
  for (let pc = 0; pc < 12; pc += 1) {
    const natural = LETTERS.find((letter) => naturalPitchClass(letter) === pc);
    if (natural) {
      names.push(pitchName({ letter: natural, alter: 0, octave: 4 }));
      continue;
    }
    // A black key: name it from the letter one semitone away in the chosen
    // direction. That letter is always natural, so no double accidental appears.
    const neighbour = LETTERS.find(
      (letter) => naturalPitchClass(letter) === (((pc - alter) % 12) + 12) % 12,
    )!;
    names.push(pitchName({ letter: neighbour, alter, octave: 4 }));
  }
  return names;
}

export function pitchClassName(pitchClass: number, preference: AccidentalPreference): string {
  const index = ((pitchClass % 12) + 12) % 12;
  return PITCH_CLASS_NAMES[preference][index];
}

export const PITCH_CLASSES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/** Every letter+alter pair that sounds as the given pitch class, alterations within ±2. */
function spellingsOf(pitchClass: number): { letter: Letter; alter: number }[] {
  const target = ((pitchClass % 12) + 12) % 12;
  const out: { letter: Letter; alter: number }[] = [];
  for (const letter of LETTERS) {
    for (let alter = -2; alter <= 2; alter++) {
      if ((((naturalPitchClass(letter) + alter) % 12) + 12) % 12 === target) {
        out.push({ letter, alter });
      }
    }
  }
  return out;
}

/**
 * Choose how to spell a chosen pitch class, given the role it plays in the chord.
 *
 * "The 3rd is the pitch class 3 semitones above C" is ambiguous on paper — D♯ or
 * E♭ — but only one of them makes the chord spell in thirds without collecting
 * double accidentals. That is the one a harmony class expects.
 */
export function chooseSpelling(
  pitchClass: number,
  quality: ChordQuality,
  tone: ChordTone,
  preference: AccidentalPreference,
): { known: Pitch; root: Pitch; chord: Pitch[]; theoretical: boolean } {
  const candidates = spellingsOf(pitchClass);
  let best: {
    known: Pitch;
    root: Pitch;
    chord: Pitch[];
    theoretical: boolean;
    penalty: number;
  } | null = null;

  for (const spelling of candidates) {
    const known: Pitch = { ...spelling, octave: 4 };
    const solved = solveRootFromDegree(quality, tone, known);
    const chord = spellChord(solved.root, quality);

    let penalty = 0;

    // The root carries the chord's identity, so its spelling dominates everything
    // else. Asking for pitch class 0 must give C, never B♯ — even when the quality
    // then forces ugly notes further up the stack.
    if (Math.abs(solved.root.alter) > 2) penalty += 1000;
    penalty += Math.abs(solved.root.alter) * 25;
    if (isAwkwardSpelling(solved.root)) penalty += 10;

    // The given note should also read naturally for the pitch class picked.
    penalty += Math.abs(known.alter) * 6;
    if (isAwkwardSpelling(known)) penalty += 8;

    // Awkward notes elsewhere in the chord are usually *required* by the quality
    // — a fully diminished seventh genuinely contains a double flat — so they are
    // only a mild tiebreak, never a reason to rename the chord.
    for (const p of chord) {
      if (p === chord[0]) continue;
      if (Math.abs(p.alter) >= 2) penalty += 2;
      else if (isAwkwardSpelling(p)) penalty += 1;
    }

    // Finally, honour the learner's ♯/♭ preference.
    if (preference === 'sharp' && known.alter < 0) penalty += 1;
    if (preference === 'flat' && known.alter > 0) penalty += 1;

    if (!best || penalty < best.penalty) {
      best = { known, root: solved.root, chord, theoretical: solved.theoretical, penalty };
    }
  }

  const chosen = best!;
  return {
    known: chosen.known,
    root: chosen.root,
    chord: chosen.chord,
    theoretical: chosen.theoretical,
  };
}

/**
 * The register each clef sits most comfortably in, as a MIDI number.
 *
 * Read off the staff rather than written down: position 4 is the middle line by
 * definition of the five-line stave, so this cannot disagree with where the
 * engraver actually puts a note. Hard-coded, it would be a second statement of
 * what each clef means, and a clef added later would silently keep treble's.
 */
const MIDDLE_LINE = 4;
const CLEF_CENTRE: Record<Clef, number> = Object.fromEntries(
  CLEFS.map((clef) => [clef, midi(positionToPitch(clef, MIDDLE_LINE))]),
) as Record<Clef, number>;

/**
 * Put the chord in a register that reads well on the chosen clef: close to the
 * middle line, and not so high or low that it disappears into ledger lines.
 */
export function voiceForClef(root: Pitch, quality: ChordQuality, clef: Clef): Pitch[] {
  const centre = CLEF_CENTRE[clef];
  let best: { chord: Pitch[]; cost: number } | null = null;

  for (let octave = 1; octave <= 6; octave++) {
    const chord = spellChord({ ...root, octave }, quality);
    const notes = chord.map(midi);
    const mean = notes.reduce((a, b) => a + b, 0) / notes.length;

    // Distance of the chord's centre of gravity from the clef's middle line,
    // plus a penalty for any voice straying far outside the staff.
    let cost = Math.abs(mean - centre);
    for (const n of notes) {
      const strayAbove = n - (centre + 14);
      const strayBelow = centre - 14 - n;
      if (strayAbove > 0) cost += strayAbove * 1.5;
      if (strayBelow > 0) cost += strayBelow * 1.5;
    }

    if (!best || cost < best.cost) best = { chord, cost };
  }

  return best!.chord;
}

/** Convenience: pitch class + degree + quality + clef -> everything the UI needs. */
export function buildChordFromDegree(
  pitchClass: number,
  quality: ChordQuality,
  tone: ChordTone,
  clef: Clef,
  preference: AccidentalPreference,
): { root: Pitch; known: Pitch; voicing: Pitch[]; theoretical: boolean } {
  const { known, root, theoretical } = chooseSpelling(pitchClass, quality, tone, preference);
  const voicing = voiceForClef(root, quality, clef);
  return { root, known, voicing, theoretical };
}

/** Sensible default octave for a clef, used when something needs a bare register. */
export function defaultOctave(clef: Clef): number {
  return CLEF_INFO[clef].defaultRootOctave;
}
